package pl.nickers.match3.ecs

import pl.nickers.match3.AudioPlayer
import pl.nickers.match3.GameStorage
import pl.nickers.match3.SoundEvent

/**
 * Reads every [SoundEvent] queued this frame via [enqueueAudioEvent], dispatches
 * them to [AudioPlayer], then clears the queue.
 *
 * Must be registered **last** in the [World] builder (after [RenderSystem]) so that
 * all other systems have had a chance to enqueue their events before we consume them.
 *
 * Deduplication is handled by the [AudioQueueComponent]'s internal [Set] — if two
 * systems enqueue the same event (e.g. two simultaneous matches), the sound plays
 * exactly once per frame.
 *
 * Playback is gated by [GameStorage.soundEnabled] — when muted, the queue is
 * drained silently and the audio player is not called.
 */
internal class AudioSystem(
    private val player: AudioPlayer,
    private val storage: GameStorage = GameStorage(),
) : BaseSystem() {
    private lateinit var audioQueueMapper: ComponentMapper<AudioQueueComponent>

    override fun initialize() {
        audioQueueMapper = world.mapper()
    }

    override fun processSystem() {
        val boardEntity = world.boardEntityId() ?: return
        val queue = audioQueueMapper[boardEntity] ?: return
        if (storage.soundEnabled) {
            queue.events.forEach { player.playSfx(it) }
        }
        audioQueueMapper.remove(boardEntity)
    }
}

// ---------------------------------------------------------------------------
// World extension — used by game systems to queue audio without coupling to
// AudioSystem directly.
// ---------------------------------------------------------------------------

/**
 * Enqueues [event] to be played by [AudioSystem] at the end of the current frame.
 *
 * Safe to call from any system; duplicate events within the same frame are
 * silently discarded (the backing collection is a [Set]).
 */
fun World.enqueueAudioEvent(event: SoundEvent) {
    val boardEntity = boardEntityId() ?: return
    val queue = getComponent(boardEntity, AudioQueueComponent::class)
        ?: AudioQueueComponent().also { addComponent(boardEntity, it) }
    queue.events.add(event)
}
